import { ReactNode, useLayoutEffect, useMemo } from 'react';
import { useQueryClient, useSuspenseQueries } from '@tanstack/react-query';
import { prefetchTakerMarketsQueries } from '@/enterprise/data/useTakerMarkets';
import i18n, { resolveLanguageCode } from '@/i18n';
import { fetchExchangeRate } from '@/shared/data/useExchangeRate';
import { fetchUserDetails } from '@/shared/data/useUserDetails';
import useUTMParams from '@/utils/useUTMParams';

const Hydrate = ({ children }: { children: ReactNode }) => {
  const { handleUTMParams } = useUTMParams();
  const queryClient = useQueryClient();

  // set any UTM params sent via URL in session storage
  handleUTMParams();

  // Hydrate user details and USD exchange rates concurrently. They are independent and both
  // gate first paint, so running them in parallel (useSuspenseQueries) avoids the waterfall
  // of two sequential useSuspenseQuery calls. Keys/fns match useUserDetails/useExchangeRate
  // so the cache is shared with those hooks elsewhere.
  // Exchange rates are used across the app for conversions.
  const [{ data: user }] = useSuspenseQueries({
    queries: [
      { queryKey: ['user-details'], queryFn: fetchUserDetails },
      { queryKey: ['exchange-rates', 'USD'], queryFn: () => fetchExchangeRate('USD') },
    ],
  });

  const userLocale = useMemo(() => user?.me?.personalInfo?.locale, [user?.me?.personalInfo?.locale]);

  // Start taker-markets + user-suppliers as soon as bootstrap data is warm — overlaps
  // account-feature-configs, Split, and nav artifacts (no data dependency on artifacts).
  useLayoutEffect(() => {
    void prefetchTakerMarketsQueries(queryClient);
  }, [queryClient]);

  useLayoutEffect(() => {
    if (!userLocale) {
      // Profile locale not yet available — preserve whatever language i18next already resolved
      // (e.g. from localStorage or navigator). Only override once a real locale arrives.

      return;
    }

    // Supported locale → use it; present but unsupported → fall back to 'en'.
    i18n.changeLanguage(resolveLanguageCode(userLocale) ?? 'en');
  }, [userLocale]);

  return children;
};

export default Hydrate;
